import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import type { CallAnalysisTagDefinitionChoice } from '@reach/shared-types/call-analysis-tag-definitions';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { ColorFieldRow } from './ColorFieldRow';
import { IconPicker } from './IconPicker';
import { choicePillInlineStyles } from './tag-choice-pill-utils';
import { resolveTagIcon } from './tag-icon-utils';
import type { ChoiceFields } from './tag-definition-choice-utils';
import styles from './TagDefinitionChoiceCard.module.css';

export interface ChoiceEditInputIds {
  displayName: string;
  tooltipDescription: string;
  background: string;
  textColor: string;
  icon: string;
}

export interface TagDefinitionChoiceCardProps {
  choice: CallAnalysisTagDefinitionChoice;
  previewChoice: CallAnalysisTagDefinitionChoice;
  isEditing: boolean;
  onStartEdit: () => void;
  onRemove: () => void;
  editInputIds: ChoiceEditInputIds;
  editFields: ChoiceFields;
  onEditFieldChange: <K extends keyof ChoiceFields>(field: K, value: ChoiceFields[K]) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
}

export const TagDefinitionChoiceCard: React.FC<TagDefinitionChoiceCardProps> = ({
  choice,
  previewChoice,
  isEditing,
  onStartEdit,
  onRemove,
  editInputIds,
  editFields,
  onEditFieldChange,
  onCancelEdit,
  onSaveEdit,
}) => {
  const ResolvedIcon = previewChoice.icon ? resolveTagIcon(previewChoice.icon) : null;
  const pillStyle = choicePillInlineStyles(previewChoice.color, previewChoice.textColor);
  const pillLabel = previewChoice.displayName ?? previewChoice.value;

  return (
    <div className={styles.choiceCard}>
      <div className={styles.choiceCardRow}>
        <div className={styles.choicePillWrap}>
          <span
            className={styles.choicePill}
            style={pillStyle}
            title={previewChoice.tooltipDescription ?? previewChoice.displayName ?? previewChoice.value}>
            {ResolvedIcon ? (
              <span className={styles.choicePillIcon} title={previewChoice.icon}>
                <ResolvedIcon aria-hidden />
              </span>
            ) : null}
            <span className={styles.choicePillLabel}>{pillLabel}</span>
          </span>
        </div>
        <div className={styles.choiceActions}>
          {!isEditing ? (
            <button
              type="button"
              onClick={onStartEdit}
              className={styles.choiceEditButton}
              aria-label={`Edit ${choice.value}`}>
              <Pencil />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onRemove}
            className={styles.choiceRemoveButton}
            aria-label={`Remove ${choice.value}`}>
            <Trash2 />
          </button>
        </div>
      </div>
      {isEditing ? (
        <div className={styles.choiceEditPanel}>
          <p className={styles.choiceReadonlyValue}>
            Value: <code>{choice.value}</code>
          </p>
          <Label htmlFor={editInputIds.displayName}>Display name (optional)</Label>
          <Input
            id={editInputIds.displayName}
            value={editFields.displayName}
            onChange={e => onEditFieldChange('displayName', e.target.value)}
            placeholder="e.g. New Lead"
            maxLength={255}
          />
          <Label htmlFor={editInputIds.tooltipDescription}>Tooltip description (optional)</Label>
          <textarea
            id={editInputIds.tooltipDescription}
            value={editFields.tooltipDescription}
            onChange={e => onEditFieldChange('tooltipDescription', e.target.value)}
            placeholder="Plain-English explanation shown on hover..."
            rows={2}
            maxLength={1024}
            className={styles.choiceEditTextarea}
          />
          <ColorFieldRow
            label="Background"
            colorValue={editFields.color}
            onColorValueChange={v => onEditFieldChange('color', v)}
            inputId={editInputIds.background}
          />
          <ColorFieldRow
            label="Text"
            colorValue={editFields.textColor}
            onColorValueChange={v => onEditFieldChange('textColor', v)}
            inputId={editInputIds.textColor}
          />
          <IconPicker
            value={editFields.icon}
            onChange={v => onEditFieldChange('icon', v)}
            id={editInputIds.icon}
          />
          <div className={styles.choiceEditActions}>
            <Button type="button" variant="outline" size="sm" onClick={onCancelEdit}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={onSaveEdit}>
              Save choice
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
